/**
 * @fileoverview Custom hook for date formatting based on user preferences
 * Provides centralized date formatting using the user's configured date format
 */

import { useCompanyDetails } from '@/features/company-details/hooks/use-company-details';
import { format } from 'date-fns';

/**
 * Date format mapping from user preference to date-fns format strings
 */
const DATE_FORMAT_MAP: Record<string, string> = {
  'DD.MM.YYYY': 'dd.MM.yyyy',
  'MM/DD/YYYY': 'MM/dd/yyyy',
  'YYYY-MM-DD': 'yyyy-MM-dd',
  'DD/MM/YYYY': 'dd/MM/yyyy',
  'DD-MM-YYYY': 'dd-MM-yyyy',
};

/**
 * Default date format (German format)
 */
const DEFAULT_DATE_FORMAT = 'dd.MM.yyyy';

/**
 * Hook to get the current date format and formatting functions
 * 
 * @returns Object containing formatDate function and current format string
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { formatDate } = useDateFormat();
 *   
 *   return <div>{formatDate(new Date())}</div>;
 * }
 * ```
 */
export function useDateFormat() {
  const { data: companyDetails } = useCompanyDetails();
  
  // Get the date-fns format string based on user preference
  const dateFormatString = companyDetails?.dateFormat 
    ? DATE_FORMAT_MAP[companyDetails.dateFormat] || DEFAULT_DATE_FORMAT
    : DEFAULT_DATE_FORMAT;

  /**
   * Format a date using the user's preferred format
   * 
   * @param date - Date object or ISO string to format
   * @returns Formatted date string
   */
  const formatDate = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    
    // Check for invalid dates
    if (isNaN(d.getTime())) {
      console.error('Invalid date provided to formatDate:', date);
      return '';
    }
    
    return format(d, dateFormatString);
  };

  /**
   * Format a date with time using the user's preferred format
   * 
   * @param date - Date object or ISO string to format
   * @returns Formatted date and time string
   */
  const formatDateTime = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    
    // Check for invalid dates
    if (isNaN(d.getTime())) {
      console.error('Invalid date provided to formatDateTime:', date);
      return '';
    }
    
    // Append time format to date format
    return format(d, `${dateFormatString} HH:mm`);
  };

  return {
    formatDate,
    formatDateTime,
    dateFormat: companyDetails?.dateFormat || 'DD.MM.YYYY',
    dateFormatString,
  };
}
